import ISagaRequirementState from "models/ISagaRequirementState";
import ISagaState, { SagaKind } from "models/ISagaState";
import theme from "Theme/AppTheme";

export type SagaStatus = "complete" | "inProgress";

export interface SagaSummary {
    statusLabel: string;
    statusDetail: string;
    statusColor: string;
    statusBackground: string;
    status: SagaStatus;
    metCount: number;
    requiredCount: number;
    progress: number;
}

export interface SagaTotals {
    total: number;
    complete: number;
    inProgress: number;
}

/**
 * Saga and requirement ids arrive as display names already — classifier names,
 * outbound configuration names, workflow condition names, or fixed labels like
 * "Reporting Saga V2". They are shown as-is.
 *
 * Do not add PascalCase word-splitting here. It corrupts real names far more
 * often than it helps: "RBCs" became "RB Cs", "ML-SentimentV2" became
 * "ML-Sentiment V2", and "ML-AdverseEvents" became "ML-Adverse Events". Only
 * underscores are replaced, for keys like "CallDriversTheme_Populated".
 */
export function formatSagaName(value: string) {
    if (!value) return value;
    return value.replace(/_/g, " ").trim();
}

/**
 * A saga is either finished or still waiting on requirements — there is no
 * third "started but has no requirements" state.
 *
 * An incomplete saga always has requirement rows, because completion is
 * evaluated *from* those rows: each saga's IsComplete reads its requirement key
 * (e.g. RequiredClassifiersForWorkflowCondition) and walks the classifiers it
 * lists. With no rows there would be nothing to evaluate and the saga could
 * never finish. SagaManager.FinishSaga then deletes the rows on completion, and
 * the API derives `complete` from durable evidence elsewhere (a FastReporting
 * row, the AMD status, classifier results) while skipping requirement
 * generation for anything it already knows is complete.
 *
 * So an empty list on an incomplete saga means the API failed to expand rows
 * that do exist — a backend bug, not a lifecycle stage. Do not add a UI state
 * for it; fix the expansion instead. One such bug was the workflow-condition
 * requirement key missing from the controller's requirementsListKeys.
 */
export function getSagaStatus(saga: ISagaState): SagaStatus {
    return saga.complete ? "complete" : "inProgress";
}

export function summarizeSaga(saga: ISagaState): SagaSummary {
    const status = getSagaStatus(saga);

    // Waived requirements are not blocking, so they count as satisfied for
    // progress purposes rather than dragging the bar down forever.
    const required = saga.sagaRequirementsState;
    const requiredCount = required.length;
    const metCount = required.filter(
        (requirement) => requirement.isMet || requirement.isNoLongerRequired,
    ).length;

    if (status === "complete") {
        return {
            status,
            statusLabel: "Complete",
            statusDetail:
                "This saga finished. Its tracking records were cleared on completion, so individual requirements are no longer retained.",
            statusColor: theme.palette.success.main,
            statusBackground: theme.palette.success.light,
            metCount: requiredCount,
            requiredCount,
            progress: 100,
        };
    }

    return {
        status,
        statusLabel: "In Progress",
        statusDetail: `${
            requiredCount - metCount
        } requirement(s) still outstanding.`,
        statusColor: theme.palette.warning.main,
        statusBackground: theme.palette.warning.light,
        metCount,
        requiredCount,
        progress: requiredCount > 0 ? (metCount / requiredCount) * 100 : 0,
    };
}

export function getSagaTotals(sagas: ISagaState[]): SagaTotals {
    return sagas.reduce<SagaTotals>(
        (totals, saga) => {
            const status = getSagaStatus(saga);
            return {
                total: totals.total + 1,
                complete: totals.complete + (status === "complete" ? 1 : 0),
                inProgress:
                    totals.inProgress + (status === "inProgress" ? 1 : 0),
            };
        },
        { total: 0, complete: 0, inProgress: 0 },
    );
}

/**
 * Incomplete sagas sort first, since those are the ones still holding the AMD up
 * and the only ones carrying requirement detail.
 */
export function sortSagasForDisplay(sagas: ISagaState[]): ISagaState[] {
    const rank: Record<SagaStatus, number> = {
        inProgress: 0,
        complete: 1,
    };

    return [...sagas].sort((a, b) => {
        const delta = rank[getSagaStatus(a)] - rank[getSagaStatus(b)];
        return delta !== 0 ? delta : a.sagaName.localeCompare(b.sagaName);
    });
}

/**
 * Orders a saga's requirements so the blocking ones read first: outstanding,
 * then waived, then met. Waived sits in the middle because it is not blocking
 * but is the state most often worth checking when a saga looks stuck.
 *
 * Ties keep the order the API returned rather than sorting by name, so a
 * requirement does not move between refreshes for no visible reason.
 */
export function sortRequirementsForDisplay(
    requirements: ISagaRequirementState[],
): ISagaRequirementState[] {
    const rank = (requirement: ISagaRequirementState) => {
        if (requirement.isNoLongerRequired) return 1;
        return requirement.isMet ? 2 : 0;
    };

    return [...requirements]
        .map((requirement, index) => ({ requirement, index }))
        .sort(
            (a, b) =>
                rank(a.requirement) - rank(b.requirement) || a.index - b.index,
        )
        .map(({ requirement }) => requirement);
}

export interface SagaGroup {
    kind: SagaKind;
    title: string;
    description: string;
    sagas: ISagaState[];
    totals: SagaTotals;
}

/**
 * Group order follows the pipeline: the two fixed sagas that gate the file
 * itself, then the configured fan-out (emissions, workflows), then the
 * classifier dependencies, which are the most numerous and least often read.
 */
const SAGA_GROUP_ORDER: {
    kind: SagaKind;
    title: string;
    description: string;
}[] = [
    {
        kind: "pipeline",
        title: "Pipeline",
        description:
            "Ready to Sample and Reporting — the sagas that gate this file's own processing.",
    },
    {
        kind: "outboundMessaging",
        title: "Outbound Emissions",
        description:
            "Outbound messaging configurations waiting to emit for this file.",
    },
    {
        kind: "workflowCondition",
        title: "Workflows",
        description:
            "Model workflow conditions evaluated against this file's results.",
    },
    {
        kind: "classifierDependency",
        title: "Classifier Dependencies",
        description:
            "Models that wait on other models before they can run.",
    },
];

/** A saga from a response predating sagaKind is treated as a classifier dependency. */
function getSagaKind(saga: ISagaState): SagaKind {
    return saga.sagaKind ?? "classifierDependency";
}

/** Groups sagas by kind, dropping groups with nothing in them. */
export function groupSagasForDisplay(sagas: ISagaState[]): SagaGroup[] {
    return SAGA_GROUP_ORDER.map(({ kind, title, description }) => {
        const groupSagas = sortSagasForDisplay(
            sagas.filter((saga) => getSagaKind(saga) === kind),
        );

        return {
            kind,
            title,
            description,
            sagas: groupSagas,
            totals: getSagaTotals(groupSagas),
        };
    }).filter((group) => group.sagas.length > 0);
}
